import { CalendarListing, Experience } from '@/types';
import { endOfMonth, formatDateWithDayName, startOfMonth } from '@/utils';
import {
  Box,
  Container,
  Typography,
  useTheme,
} from '@silverstein-properties/inspirelabs-ui';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { ListItem } from './ListItem';
import { useMerchantListings } from '@/hooks';
import { formatListing } from '@/utils/schedulingUtils';
import { CircularProgress } from '@mui/material';
import { CustomPagination } from '@/components';
import { DEFAULT_SEARCH_PARAMS } from '@/constants';
import { PaginationState } from '@tanstack/react-table';
import { EVENTS_FILTERS } from '@/constants';

type GroupedListingsType = {
  [key: string]: Array<CalendarListing>;
};

function groupListingsByDay(listings: CalendarListing[]): GroupedListingsType {
  const groupedListings: GroupedListingsType = {};

  for (const listing of listings) {
    if (!listing.start) {
      continue;
    }
    const date = new Date(listing.start);
    const key = date.toISOString().split('T')[0]; // Format date as YYYY-MM-DD

    if (!groupedListings[key]) {
      groupedListings[key] = [];
    }
    groupedListings[key].push(listing);
  }

  return groupedListings;
}

export const ListView = ({
  selectedDate,
  selectedProductIds,
  eventsFilter,
  products,
  onEventClick,
}: {
  selectedDate: Date;
  selectedProductIds: string[];
  eventsFilter: string;
  products: Experience[];
  onEventClick?: (id: string) => void;
}) => {
  const theme = useTheme();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: DEFAULT_SEARCH_PARAMS.PAGE,
    pageSize: DEFAULT_SEARCH_PARAMS.PAGE_SIZE,
  });

  const {
    data: merchantListingsPaginated,
    isLoading: isMerchantListingsLoading,
    isFetching,
  } = useMerchantListings(
    {
      startsAt: startOfMonth(selectedDate),
      endsAt: endOfMonth(selectedDate),
      productIds: selectedProductIds,
      hasBookings: eventsFilter === EVENTS_FILTERS.BOOKED,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
    },
    selectedProductIds.length > 0
  );

  useEffect(() => {
    setPagination({
      pageIndex: DEFAULT_SEARCH_PARAMS.PAGE,
      pageSize: DEFAULT_SEARCH_PARAMS.PAGE_SIZE,
    });
  }, [selectedDate, selectedProductIds, eventsFilter]);

  const formattedEvents = useMemo(
    () => formatListing(merchantListingsPaginated?.data || [], products || []),
    [merchantListingsPaginated?.data, products]
  );

  const listingsByDay = groupListingsByDay(formattedEvents);

  const innerNodes: ReactNode[] = [];
  Object.keys(listingsByDay || {})?.forEach(dayKey => {
    const dayListings = listingsByDay[dayKey];

    if (dayListings) {
      // sparse array, so might be undefined
      const dayStr = formatDateWithDayName(dayKey);

      // append a day header
      innerNodes.push(
        <Typography variant="subtitle2" key={dayStr}>
          {dayStr}
        </Typography>
      );

      for (const listing of dayListings) {
        innerNodes.push(
          <ListItem
            key={listing.id}
            event={listing}
            startDate={new Date(listing.start)}
            endDate={listing.end ? new Date(listing.end) : undefined}
            onEdit={() => onEventClick?.(listing.id)}
          />
        );
      }
    }
  });

  if (isMerchantListingsLoading) {
    return (
      <Container
        sx={{
          paddingY: theme.spacers.xxl4,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Box
      sx={{
        overflowY: 'auto',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacers.m,
      }}
    >
      {innerNodes}
      <CustomPagination
        show={true}
        currentPage={pagination.pageIndex}
        isLoading={isMerchantListingsLoading || isFetching}
        pageSize={pagination.pageSize}
        totalItems={merchantListingsPaginated?.metadata?.totalItems || 0}
        onPageChange={page => setPagination({ ...pagination, pageIndex: page })}
      />
    </Box>
  );
};
